import React, { forwardRef } from "react"
import classNames from "classnames"

import Icon, { IconProps } from "../Icon"
import { iconNames } from "../Icon/Icon.config"
import { MutuallyExclude, getExclusiveElement } from "../utils/mutuallyExclusive"
import { TButtonVariants, buttonVariants } from "./Button.config"
import { generateSpacingClasses } from "@utils/spacing"

import "./Button.css"

const baseClass = "wb-button"

/* Instead of a "type" prop that takes oneOf(["primary", "secondary", etc]),
* each type gets passed as a separate bool prop.
* This is for a nicer external API, as buttons are used very often.
* It's easier to write <Button primary> than <Button type="primary">
* You should only pass one type prop into the component */
type VariantProps = MutuallyExclude<TButtonVariants, keyof TButtonVariants>

type BaseProps = {
  /** For text only buttons, a variant to render it as a link (primary colour) is availble */
  textOnlyVariant?: "link" | "primary",
  /** Mirrors the `type` attribute of the HTML `<button>` tag. */
  // type: PropTypes.string,
  /** Display the specified icon name (delegated to `<Icon>`) to the left of the button text. */
  iconBefore?: React.ReactNode | typeof iconNames[number],
  /** Display the specified icon name (delegated to `<Icon>`) to the right of the button text. */
  iconAfter?: React.ReactNode | typeof iconNames[number],
  /** Specify the button size */
  size?: "small" | "medium",
  /** Override the icon size. The icon size is chosen based on the button style. */
  iconSize?: IconProps["size"],
  /** Changes the button's role attribute (`<button role="link">`). This is set * automatically when the textOnlyVariant prop is "link" */
  isLink?: boolean,
  /** Specify url for when the button is marked as isLink */
  href?: string,
  /** Button click handler */
  onClick?: React.MouseEventHandler<HTMLButtonElement>,
  /** If provided, this class name will be attached to the outer `<button>` node. */
  className?: string,
  /** If provided, this class name will be attached to the innermost `<span>` containing the button text. */
  textNodeClassName?: string
  /** React children. */
  children?: React.ReactNode,
  /** HTML Button type. */
  type?: HTMLButtonElement['type']
  /** Optional explicit variant that will override. */
  variant?: typeof buttonVariants[number]
  /** Margin is a shorthand prop for applying margin to the button. */
  margin?: string | object
  /** Padding is a shorthand prop for applying padding to the button. */
  padding?: string | object
}

type ButtonProps = BaseProps & VariantProps

/**
 * The `<Button>` component renders buttons in various styles, taking into account
 * the hover, focus and disabled states.
 *
 * Any further props not defined will be passed as-is to the outer `<button>` node.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  textOnlyVariant,
  type,
  size = "medium",
  iconBefore,
  iconAfter,
  iconSize,
  isLink = false,
  href,
  onClick,
  className,
  textNodeClassName,
  children,
  margin,
  padding,
  variant,
  ...variants
}, ref)  => {
  const buttonType = variant || getExclusiveElement(variants, buttonVariants)
  const spacingClasses = generateSpacingClasses(padding, margin)

  const classes = classNames(
    getButtonClasses({
      buttonType,
      textOnlyVariant,
      size,
      iconBefore,
      iconAfter,
      className,
    }),
    spacingClasses
  )

  const isALink = isLink || textOnlyVariant === "link"
  const hrefOnClick = href != null ? () => window.location.href = href : onClick;

  return (
    <button
      ref={ref || null}
      type={type || "button"}
      role={isALink ? "link" : "button"}
      className={classes}
      onClick={isLink ? hrefOnClick : onClick}>
      {getButtonContents(iconBefore, iconAfter, iconSize, textNodeClassName, children)}
    </button>
  )
})

Button.displayName = "Button"

function getButtonClasses({
  buttonType,
  textOnlyVariant,
  size,
  iconBefore,
  iconAfter,
  className
}) {
  return classNames(baseClass, classNameForButtonType(buttonType), {
    [`${baseClass}--only-icon`]: buttonType == "iconOnly",
    [`${baseClass}--with-icon`]: Boolean(iconBefore) || Boolean(iconAfter),
    [`${baseClass}--with-icon-left`]: Boolean(iconBefore),
    [`${baseClass}--with-icon-right`]: Boolean(iconAfter),
    [`${baseClass}--${size}`]: true,
    [`${baseClass}--text-only--${textOnlyVariant}`]: (
      buttonType === "textOnly" && textOnlyVariant
    )
  }, className)
}

function classNameForButtonType(buttonType) {
  switch (buttonType) {
    case "text_only":
      return `${baseClass}--text-only`
    default:
      return `${baseClass}--${buttonType}`
  }
}

function getButtonContents(
  iconBefore,
  iconAfter,
  iconSize,
  textNodeClassName,
  children
) {
  const iconNode = iconName => (
    <Icon name={iconName} size={iconSize || "medium"} className={`${baseClass}__icon`} />
  )

  const iconBeforeNode = typeof iconBefore == "string" ? iconNode(iconBefore) : iconBefore
  const iconAfterNode = typeof iconAfter == "string" ? iconNode(iconAfter) : iconAfter

  const classes = classNames(`${baseClass}__text`, textNodeClassName)

  return (
    <span>
      {iconBeforeNode}
      {children && <span className={classes}>{children}</span>}
      {iconAfterNode}
    </span>
  )
}

export { Button, buttonVariants }
export type { ButtonProps, VariantProps }
